// Integrates rain-rate frames (mm/h on a shared grid) into a total (mm) over
// exactly [startMs, endMs]. Pure typed-array code: no DOM, no workers.

export const MAX_GAP_MS = 20 * 60 * 1000;
const MS_PER_HOUR = 3600 * 1000;

// Closed-form integral of the linear blend (1-a)*R0 + a*R1 for a in [a0, a1].
export function linearPairIntegrator(r0, r1, a0, a1, hours, out) {
  const w0 = hours * ((a1 - a0) - (a1 * a1 - a0 * a0) / 2);
  const w1 = hours * ((a1 * a1 - a0 * a0) / 2);
  for (let i = 0; i < out.length; i++) out[i] += w0 * r0[i] + w1 * r1[i];
}

// frames: [{ tMs, rate }] sorted by time; `rate` may be a function returning
// the Float32Array so callers can keep frames packed until needed.
// pairIntegrator(r0, r1, a0, a1, hours, out, pairIndex) adds the pair's
// contribution over blend fractions [a0, a1] into `out`.
export function accumulate({
  frames,
  startMs,
  endMs,
  size,
  maxGapMs = MAX_GAP_MS,
  pairIntegrator = linearPairIntegrator,
}) {
  const total = new Float32Array(size);
  const gaps = [];
  const pairs = [];
  const getRate = (f) => (typeof f.rate === "function" ? f.rate() : f.rate);
  const addGap = (a, b) => {
    if (b > a) gaps.push({ startMs: a, endMs: b });
  };

  if (!frames.length) {
    addGap(startMs, endMs);
    return { total: total.fill(NaN), gaps, pairs, coveredMs: 0 };
  }
  addGap(startMs, Math.min(endMs, frames[0].tMs));

  let coveredMs = 0;
  let prevRate = null;
  let prevIndex = -1;
  for (let i = 0; i + 1 < frames.length; i++) {
    const a = frames[i];
    const b = frames[i + 1];
    const t0 = Math.max(a.tMs, startMs);
    const t1 = Math.min(b.tMs, endMs);
    if (t1 <= t0) continue;
    const dt = b.tMs - a.tMs;
    if (dt > maxGapMs) {
      addGap(t0, t1);
      continue;
    }
    const r0 = prevIndex === i ? prevRate : getRate(a);
    const r1 = getRate(b);
    pairIntegrator(r0, r1, (t0 - a.tMs) / dt, (t1 - a.tMs) / dt, dt / MS_PER_HOUR, total, i);
    pairs.push({ index: i, startMs: t0, endMs: t1, intervalMs: dt });
    coveredMs += t1 - t0;
    prevRate = r1;
    prevIndex = i + 1;
  }
  addGap(Math.max(startMs, frames[frames.length - 1].tMs), endMs);

  // Cells never touched by a valid pair (outside coverage, or no pairs) stay NaN.
  if (!pairs.length) total.fill(NaN);
  return { total, gaps, pairs, coveredMs };
}
